"use client";

import { Menu, X } from "lucide-react";
import React, {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";

export function MobileNavigation({
  children,
  openLabel,
  closeLabel,
  navigationLabel,
}: {
  readonly children: ReactNode;
  readonly openLabel: string;
  readonly closeLabel: string;
  readonly navigationLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const navigationId = "mobile-site-navigation";
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current || !(event.target instanceof Node)) {
        return;
      }

      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const closeAfterNavigation = (event: ReactMouseEvent<HTMLElement>) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !(event.target instanceof Element) ||
      event.target.closest("a[href]") === null
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <div className="md:hidden" ref={rootRef}>
      <button
        type="button"
        aria-label={open ? closeLabel : openLabel}
        aria-expanded={open}
        aria-controls={navigationId}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md bg-surface px-2.5 text-primary transition-colors duration-200 hover:bg-action-secondary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        {open ? (
          <X className="size-5" aria-hidden="true" strokeWidth={2.25} />
        ) : (
          <Menu className="size-5" aria-hidden="true" strokeWidth={2.25} />
        )}
      </button>
      {open ? (
        <nav
          id={navigationId}
          aria-label={navigationLabel}
          onClick={closeAfterNavigation}
          className="fixed inset-x-0 top-[calc(64px+env(safe-area-inset-top,0px))] z-[var(--z-index-modal)] flex max-h-[calc(100dvh-64px-env(safe-area-inset-top,0px))] w-full flex-col overflow-y-auto overscroll-contain border-b border-border-subtle bg-surface px-4 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pt-3 shadow-elevation-3"
        >
          <div className="mx-auto flex w-full max-w-app flex-col gap-1.5 pb-3">
            {children}
          </div>
        </nav>
      ) : null}
    </div>
  );
}
